import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useMedicalCareContext } from "./MedicalCareContext";
import { useLocation } from "react-router-dom";

interface InterventionsContextValue {
  control: any;
  setValue: any;
  handleSubmit: any;
  setClean: Function;

  createDialog: any;
  setCreateDialog: Function;
  selectProtocol: any;
  setSelectProtocol: Function;

  setMedication: any;
  medicationOptions: any;

  selectedInterventions: any;
  setInterventionsList: Function;

  base64: any;
  setBase64: any;

  selectedItem: any;
  setSelectedItem: Function;

  expandedList: any;
  setExpandedList: Function;

  subTabValue: any;
  setSubTabValue: Function;

  medicationTabValue: any;
  setMedicationTabValue: Function;

  functions: any;
  action: any;

  isEdit: any;
  clean: any;

  appliedProtocol: any;
  setAppliedProtocol: any;
}

export const InterventionsContext = createContext<InterventionsContextValue>(
  null!
);

export function InterventionsContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { setInterventionTab, resetSignal } = useMedicalCareContext();

  const { state } = useLocation();

  const [expandedList, setExpandedList] = useState<boolean>(true);
  const [subTabValue, setSubTabValue] = useState(0);
  const [medicationTabValue, setMedicationTabValue] = useState(0);

  const methods = useForm();
  const { handleSubmit, control, reset, setValue } = methods;
  const [interventionsList, setInterventionsList] = useState<Array<any>>([]);
  const [selectedItem, setSelectedItem] = useState<number>();
  const [medication, setMedication] = useState([]);
  const [clean, setClean] = useState<boolean>(false);
  const [appliedProtocol, setAppliedProtocol] = useState<Array<any>>([]);

  const [base64, setBase64] = useState<any>();

  const [createDialog, setCreateDialog] = useState<boolean>(false);
  const [selectProtocol, setSelectProtocol] = useState<boolean>(false);

  const selectedInterventions = {
    all: interventionsList,
    medicationSpecial: interventionsList?.filter(
      (el: any) =>
        el?.tag?.toUpperCase()?.includes("VERMELHA") ||
        el?.tag?.toUpperCase()?.includes("PRETA") ||
        el?.tag?.toUpperCase()?.includes("ESPECIAL")
    ),
    medicationSimple: interventionsList?.filter(
      (el: any) =>
        (el?.type === "medication" ||
          el?.type === "supplement" ||
          el?.type === "herbalMedicine") &&
        !(
          el?.tag?.toUpperCase()?.includes("VERMELHA") ||
          el?.tag?.toUpperCase()?.includes("PRETA") ||
          el?.tag?.toUpperCase()?.includes("ESPECIAL")
        )
    ),
    service: interventionsList?.filter((el: any) => el?.type === "service"),
    procedure: interventionsList?.filter((el: any) => el?.type === "procedure"),
    certificates: interventionsList?.filter(
      (el: any) => el?.type === "certificate"
    ),
    /**
     * Questionários selecionados para o atendimento/protocolo.
     *
     * Decisão:
     * - Questionários são tratados como itens dentro de `interventionsList`
     *   com `type: "questionnaire"`, para reutilizar o fluxo existente de
     *   persistência/aplicação de protocolos.
     */
    questionnaires: interventionsList?.filter(
      (el: any) => el?.type === "questionnaire"
    ),
  };
  const functions = {
    handleAdd: (item: any) => {
      setInterventionsList((prev: any) => [...prev, item]);
      setClean(true);
    },
    handleEdit: (item: any) => {
      setInterventionsList((prevState: any) => {
        const updatedItems = [...prevState];
        const index = updatedItems.findIndex((el, i) => i === selectedItem);
        if (index !== -1) {
          const previous = updatedItems[index];
          // Edição de item já persistido no encounter → marca refact (upsert só se alterado)
          const hadPersistedResource = !!(
            previous?.medicationRequestId ||
            previous?.serviceRequestId ||
            previous?.fromEditHydration
          );
          updatedItems[index] = {
            ...item,
            medicationRequestId:
              item?.medicationRequestId ?? previous?.medicationRequestId,
            serviceRequestId:
              item?.serviceRequestId ?? previous?.serviceRequestId,
            documentReferenceId:
              item?.documentReferenceId ?? previous?.documentReferenceId,
            // Metadados de remissão (relatesTo/period do DocumentReference original)
            documentRelatesTo:
              item?.documentRelatesTo ?? previous?.documentRelatesTo,
            documentPeriod: item?.documentPeriod ?? previous?.documentPeriod,
            skipDocumentPost:
              item?.skipDocumentPost ?? previous?.skipDocumentPost,
            fromEditHydration:
              previous?.fromEditHydration || item?.fromEditHydration,
            // Mantém groupIdentifier/requisition/identifier do recurso FHIR original
            groupIdentifier:
              item?.groupIdentifier ?? previous?.groupIdentifier,
            requisition: item?.requisition ?? previous?.requisition,
            identifier: item?.identifier ?? previous?.identifier,
            refact:
              previous?.refact || item?.refact || hadPersistedResource,
            // Edição invalida assinatura/PDF anterior — TabsAssinatura regenera
            sign: false,
            signedBase64: undefined,
          };
        }

        return updatedItems;
      });
      setSelectedItem(undefined);
      setClean(true);
    },
    handleSign: (index: any, signedBase64: any) => {
      setInterventionsList((prevState: any) => {
        const updatedItems = [...prevState];

        updatedItems[index] = {
          ...updatedItems[index],
          signedBase64,
          sign: true,
        };

        return updatedItems;
      });
      setSelectedItem(undefined);
      setClean(true);
    },
    handleRemove: (index: number) => {
      setSelectedItem(undefined);

      setInterventionsList((prev: any) =>
        prev.filter((item: any, i: number) => i !== index)
      );
      // DocumentVisualization limpa/regenera via fingerprint; evita flash do PDF removido
      setBase64(undefined);
    },
    handleReorder: (reorderedList: any[]) => {
      setInterventionsList(reorderedList);
    },
    handleSelect: (item: any, index: any) => {
      setSelectedItem(index);

      // Questionários não possuem formulário de edição neste contexto.
      // Mantemos o item selecionado apenas para consistência de ações (ex.: remover),
      // mas não redirecionamos abas nem resetamos formulário.
      if (item?.type === "questionnaire") {
        return;
      }

      // Tratamentos: medication, supplement, herbalMedicine
      if (
        item?.type === "medication" ||
        item?.type === "supplement" ||
        item?.type === "herbalMedicine"
      ) {
        setInterventionTab(0);

        const inputValue = item?.inputValue?.label
          ? item?.inputValue
          : {
              ...item?.inputValue,
              label: item?.name,
              value: item?.name,
              name: item?.name,
            };

        // Define a aba correta baseada no tipo
        if (item?.type === "medication") {
          setMedicationTabValue(0);
          reset({
            medication: inputValue,
            quantity: item?.quantity?.value || item?.quantity,
            frequency: item.frequency,
            note: item.note,
            time: item.time,
            form: item.formValue,
          });
        } else if (item?.type === "supplement") {
          setMedicationTabValue(1);
          reset({
            medication: inputValue,
            quantity: item?.quantity?.value || item?.quantity,
            frequency: item.frequency,
            note: item.note,
            time: item.time,
            form: item.formValue,
          });
        } else if (item?.type === "herbalMedicine") {
          setMedicationTabValue(2);
          reset({
            medication: inputValue,
            quantity: item?.quantity?.value || item?.quantity,
            frequency: item.frequency,
            note: item.note,
            time: item.time,
            form: item.formValue,
          });
        }
      }
      if (item.type === "procedure") {
        setInterventionTab(2);
        setSubTabValue(1);
        reset({
          procedure: item.inputValue,
          note: item.note,
        });
      }
      if (item.type === "service") {
        setInterventionTab(1);
        setSubTabValue(0);
        reset({
          service: item.inputValue,
          condition: item.condition,
          category: item.category || {},
          note: item.note || "",
        });
      }
      if (item.type === "certificate") {
        setInterventionTab(3);
        reset({
          certificate: item.note,
        });
      }
    },
    handleRemoveProtocol: (index: number, value: string) => {
      setAppliedProtocol((prev: any) =>
        prev.filter((item: any, i: number) => i !== index)
      );
      setInterventionsList((prev: any) =>
        prev.filter(
          (item: any, i: number) => item?.protocol?.reference !== value
        )
      );
    },
  };

  const action =
    selectedItem === undefined ? functions?.handleAdd : functions?.handleEdit;
  const isEdit = selectedItem !== undefined;

  const medicationOptions =
    medication?.map((el: any, index: number) => {
      const item = {
        class:
          el?.extension?.find((ext: any) =>
            ext.url?.includes("ClasseTerapeutica")
          )?.valueString || "-",
        type:
          el?.extension?.find((ext: any) => ext.url?.includes("TipoProduto"))
            ?.valueString || "-",
        description:
          el?.extension?.find((ext: any) => ext.url?.includes("Substancia"))
            ?.valueString || "-",
        presentation:
          el?.extension?.find((ext: any) => ext.url?.includes("Apresentacao"))
            ?.valueString || "-",
        tag:
          el?.extension?.find((ext: any) => ext.url?.includes("Tarja"))
            ?.valueString || "-",
        name:
          el?.extension?.find((ext: any) => ext.url?.includes("Produto"))
            ?.valueString || "-",
        value: index,
        code: el?.code,
      };
      return { ...item, label: `${item?.name} - ${item.presentation}` };
    }) || [];

  // Reset do atendimento: `resetSignal` muda a cada clearRegisterData (troca de
  // atendimento e finalização), inclusive em trocas consecutivas — a flag
  // `initial` travava em true e deixava a lista do atendimento anterior viva.
  useEffect(() => {
    setInterventionsList([]);
    setAppliedProtocol([]);
    setSubTabValue(0);
    setMedicationTabValue(0);
    setBase64(undefined);
  }, [resetSignal]);

  useEffect(() => {
    if (clean) {
      reset();
      // Reset específico da tab de exames
      reset({
        service: {},
        condition: [],
        category: {},
        note: "",
      });
      setClean(false);
    }
  }, [clean, reset, state?.patientId]);


  const contextValue = {
    setInterventionsList,
    expandedList,
    setExpandedList,
    control,
    setClean,
    base64,
    setBase64,
    handleSubmit,
    setValue,
    medicationOptions,
    functions,
    selectedInterventions,
    action,
    setMedication,
    selectedItem,
    createDialog,
    setCreateDialog,
    selectProtocol,
    setSelectProtocol,
    isEdit,
    clean,
    subTabValue,
    setSubTabValue,
    setSelectedItem,
    medicationTabValue,
    setMedicationTabValue,
    appliedProtocol,
    setAppliedProtocol,
  };

  return (
    <InterventionsContext.Provider value={contextValue}>
      {children}
    </InterventionsContext.Provider>
  );
}

export function useInterventionsContext() {
  return useContext(InterventionsContext);
}
